// Attune's "Hearth" identity palette — parchment and ember amber, fixed.
//
// Attune used to derive every hue from the course theme's accent, but that
// let a violet-accented course dress the moment in exactly the indigo/purple
// AI gradient the Attune charter bans, and made the presence read differently
// in every course. Attune is one voice across the platform, so it now wears
// one palette (chosen by Dylan, 6 July 2026 — see
// Attune-Facilitator-Redesign-Handover.md).
//
// Only light/dark still follows the course theme (theme.mode, falling back to
// background luminance) so the moment doesn't flashbang a dark lesson:
//   light → Hearth   (parchment, ember accent — a tutor's desk lamp)
//   dark  → Ember    (deep umber, lamplight amber — the same room at night)
//
// Returns a flat map of CSS custom properties consumed by attune.css.

import type { Theme } from '../../components/course-builder/slides/slideTypes';
import { themes } from '../../components/course-builder/slides/slideTypes';

interface HSL {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s: s * 100, l: l * 100 };
}

function parseColor(c: string | undefined): HSL | null {
  if (!c) return null;
  const t = c.trim();
  const hex = t.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    return rgbToHsl(parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16));
  }
  const rgb = t.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const p = rgb[1].split(',').map((x) => parseFloat(x));
    if (p.length >= 3) return rgbToHsl(p[0], p[1], p[2]);
  }
  return null;
}

function isDarkBg(bg: string | undefined): boolean {
  const c = parseColor(bg);
  return c ? c.l < 50 : false;
}

export interface AttunePalette {
  dark: boolean;
  vars: Record<string, string>;
}

// Ember — the Hearth room at night. Dark course themes get it as their
// conversation surface, and the end-experience "Atelier" (wrap-up onwards)
// forces it regardless of course mode, so the built piece always arrives in
// the same room the wrap-up ended in.
const EMBER_NIGHT: AttunePalette = {
  dark: true,
  vars: {
    '--att-bg': 'radial-gradient(125% 95% at 50% -8%, hsl(24 26% 14%) 0%, hsl(24 28% 9%) 44%, hsl(25 30% 6%) 78%)',
    '--att-aurora':
      'radial-gradient(38% 32% at 22% 30%, hsl(27 62% 52% / 0.26), transparent 60%),' +
      'radial-gradient(40% 34% at 80% 22%, hsl(42 58% 56% / 0.2), transparent 62%),' +
      'radial-gradient(46% 40% at 60% 84%, hsl(12 52% 50% / 0.16), transparent 64%)',
    '--att-ink': 'hsl(35 42% 93%)',
    '--att-ink-dim': 'hsl(31 24% 72%)',
    '--att-ink-ghost': 'hsl(29 16% 50%)',
    '--att-accent': 'hsl(30 72% 62%)',
    '--att-accent-soft': 'hsl(34 74% 74%)',
    '--att-glow': 'hsl(30 70% 56% / 0.45)',
    '--att-ring': 'hsl(30 68% 58% / 0.3)',
    '--att-focus': 'hsl(30 70% 58% / 0.55)',
    '--att-focus-glow': 'hsl(30 68% 55% / 0.2)',
    '--att-send-ink': 'hsl(26 62% 12%)',
    '--att-pill-bg': 'hsl(25 30% 13% / 0.55)',
    '--att-pill-border': 'hsl(30 45% 58% / 0.2)',
    '--att-card-bg': 'hsl(25 28% 15% / 0.5)',
    '--att-card-border': 'hsl(30 38% 58% / 0.16)',
    '--att-them-bg': 'hsl(30 32% 58% / 0.05)',
    '--att-them-border': 'hsl(30 32% 58% / 0.08)',
    '--att-attune-bg': 'hsl(30 62% 56% / 0.07)',
    '--att-attune-border': 'hsl(30 62% 56% / 0.14)',
  },
};

/** The Ember night surface, unconditionally — the Atelier room the built
 *  piece lives in. */
export function attuneEmberPalette(): AttunePalette {
  return EMBER_NIGHT;
}

export function attunePalette(theme: Theme): AttunePalette {
  const dark = theme.mode === 'dark' || isDarkBg(theme.colors?.background);

  if (dark) return EMBER_NIGHT;

  // Hearth — parchment, ink stays dark for legibility.
  return {
    dark,
    vars: {
      '--att-bg': 'radial-gradient(120% 95% at 50% -8%, hsl(42 70% 99%) 0%, hsl(38 52% 95%) 46%, hsl(34 46% 91%) 78%)',
      '--att-aurora':
        'radial-gradient(38% 32% at 20% 28%, hsl(27 62% 58% / 0.18), transparent 60%),' +
        'radial-gradient(40% 34% at 82% 22%, hsl(44 58% 60% / 0.15), transparent 62%),' +
        'radial-gradient(46% 40% at 60% 86%, hsl(14 52% 58% / 0.12), transparent 64%)',
      '--att-ink': 'hsl(27 36% 14%)',
      '--att-ink-dim': 'hsl(27 22% 38%)',
      '--att-ink-ghost': 'hsl(29 16% 58%)',
      '--att-accent': 'hsl(27 58% 40%)',
      '--att-accent-soft': 'hsl(26 56% 34%)',
      '--att-glow': 'hsl(27 60% 45% / 0.35)',
      '--att-ring': 'hsl(27 58% 45% / 0.26)',
      '--att-focus': 'hsl(27 60% 45% / 0.5)',
      '--att-focus-glow': 'hsl(27 58% 50% / 0.15)',
      '--att-send-ink': 'hsl(40 70% 98%)',
      '--att-pill-bg': 'hsl(42 60% 99% / 0.75)',
      '--att-pill-border': 'hsl(27 42% 40% / 0.16)',
      '--att-card-bg': 'hsl(42 56% 99% / 0.68)',
      '--att-card-border': 'hsl(27 40% 40% / 0.12)',
      '--att-them-bg': 'hsl(42 55% 99% / 0.6)',
      '--att-them-border': 'hsl(27 38% 40% / 0.09)',
      '--att-attune-bg': 'hsl(27 58% 42% / 0.07)',
      '--att-attune-border': 'hsl(27 58% 42% / 0.14)',
    },
  };
}

// ── The Atelier slide theme ──────────────────────────────────────────────────
// What the BUILT piece wears. The generated arc used to render on the course
// theme — competent and anonymous, indistinguishable from the lesson the
// learner just finished. Attune's pieces get Attune's clothes: the Ember
// family carried into a full slide theme, applied to every interaction type
// through the renderers' ordinary theme handling (no bespoke renderers).
// Derived from an existing dark theme so typography/spacing scales stay
// coherent; only colour and voice are Attune's own.

const ATTUNE_VOICE_FONT = '"Hanken Grotesk", "Optima", "Seravek", "Segoe UI", sans-serif';

export const ATTUNE_EMBER_THEME: Theme = {
  ...themes['modern-dark'],
  name: 'Attune Ember',
  mode: 'dark',
  colors: {
    background: 'hsl(24 28% 10%)',
    surface: 'hsl(25 26% 14%)',
    text: 'hsl(38 45% 93%)',
    textMuted: 'hsl(31 24% 72%)',
    primary: 'hsl(38 72% 58%)',
    accent: 'hsl(30 72% 62%)',
    feedback: {
      correctBg: 'hsl(100 24% 16%)',
      correctBorder: 'hsl(100 26% 40%)',
      correctText: 'hsl(95 35% 80%)',
      warningBg: 'hsl(38 40% 16%)',
      warningBorder: 'hsl(38 60% 45%)',
      warningText: 'hsl(42 78% 80%)',
      incorrectBg: 'hsl(16 38% 16%)',
      incorrectBorder: 'hsl(16 55% 45%)',
      incorrectText: 'hsl(18 60% 82%)',
    },
  },
  fonts: {
    ...themes['modern-dark'].fonts,
    heading: ATTUNE_VOICE_FONT,
    body: ATTUNE_VOICE_FONT,
  },
};
